import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { lastValueFrom, Observable, take } from 'rxjs';
import { DocusignService, StorageService } from '@core/services';
import { Account as AccountService } from '@customer/account/account.svc';
import { ContactService } from '@customer/contact';
import { AuthService, PermissionsService } from '@customer/services';
import { environment as _env, environment } from 'oneok/environments/environment';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { RegisterService } from '@customer/contact/register';
import { SessionAuth } from '@legacy-models/auth/user';
import { Account } from '@legacy-models/portal/Account';
import { ContactV2 } from '@legacy-models/portal/Contact';
import { PortalRoleV2 } from '@legacy-models/portal/PortalRole';
import { Envelope } from '@legacy-models/docu-sign/ds-envelope';
import {
  AdminAcknowledgmentApiResponse,
  AdminAcknowledgmentResponse,
  ContactFilterOption,
  ContactPayload,
  ContactRolePayload,
  DocuSignAuthenticateResponse,
  DocuSignEnvelopeResponse,
  PermissionRequest,
  PermissionResponse,
  PortalRolesGroup,
  SanitizedContact,
  SanitizedPortalRole,
  Session
} from './SanitizedData';

@Injectable({
  providedIn: 'root'
})
export class Admin {
  /**
   * Class (Service) properties
   */
  // Services
  private readonly _accountSvc: AccountService = inject(AccountService);
  private readonly _authSvc: AuthService = inject(AuthService);
  private readonly _contactSvc: ContactService = inject(ContactService);
  private readonly _docusignSvc: DocusignService = inject(DocusignService);
  private readonly _http: HttpClient = inject(HttpClient);
  private readonly _registerSvc: RegisterService = inject(RegisterService);
  private readonly _permissionsSvc: PermissionsService = inject(PermissionsService);
  private readonly _storageSvc: StorageService = inject(StorageService);
  // Writable Signals
  private readonly _$isApiLoading: WritableSignal<boolean> = signal<boolean>(false);
  // Readonly Signals
  readonly $isApiLoading: Signal<boolean> = this._$isApiLoading.asReadonly();
  // Primitives
  private readonly _baseUrl: string = environment.apiUrlV2;
  readonly onComputed = {
    allAccountContacts: this._onAllAccountContacts.bind(this),
    isCanAddNewContact: this._canAddNewContact.bind(this),
    isCanSaveContacts: this._canSaveContacts.bind(this),
    longestContactNameNumChars: this._onLongestContactNameNumChars.bind(this),
    visibleAccountContacts: this._onVisibleAccountContacts.bind(this),
    updatedContacts: this._onUpdatedContacts.bind(this)
  };
  private readonly _portalRoleNameMap: Record<string, string> = {
    admin: 'Admin',
    'measured volumes user': 'Measured Volume',
    'gas settlement user': 'Settlement Statements',
    'check detail user': 'Check Details',
    'ngp user': 'Requests',
    'outage user': 'Outages',
    'invoice user': 'Invoices'
  };
  private readonly _session: Session = {
    account: {
      accountAccessPortalRoleId: '',
      esaaStatus: '',
      id: '',
      isEsaaSigned: false,
      name: ''
    },
    contact: {
      id: '',
      isAdmin: true,
      isAdminAgreementAcknowledged: false,
      isEmployee: false
    }
  };
  private readonly _doc = inject(DOCUMENT);

  constructor() {
    this._storageSvc.currentAccount$.subscribe(account => this._onSessionDefined('account', account));
    this._storageSvc.currentUser$.subscribe(user => this._onSessionDefined('user', user));
  }

  /**
   * @description Utility method "freezes" a JS Object so it cannot
   *   be manipulated in any way (except if it's **redefined**.)
   * @param o `Object`
   * @returns `typeof o`
   */
  deepFreeze<T extends object>(o: T): T {
    const PROP_NAMES = Reflect.ownKeys(o);
    for (const NAME of PROP_NAMES) {
      const VALUE = (o as Record<PropertyKey, unknown>)[NAME];
      if (VALUE && typeof VALUE === 'object' && VALUE !== null) {
        this.deepFreeze(VALUE);
      }
    }
    return Object.freeze(o);
  }

  /**
   * @description
   */
  private _buildContactPermissionApiRequestsParameters(updatableContacts: ContactPayload[]): PermissionRequest[] {
    return updatableContacts
      .map(({ id: contactEmail, roles: updatableRoles }) => {
        const REQS: PermissionRequest[] = [];
        updatableRoles.forEach(({ id: roleId, selected: isRoleAdded }) => {
          REQS.push({
            email: contactEmail,
            roleId,
            http: isRoleAdded ? this._contactSvc.addRoleToAccountContact : this._contactSvc.deleteRoleFromAccountContact
          });
        });
        return REQS;
      })
      .flat();
  }

  /**
   * @description
   */
  saveContactPermissions(updatableContacts: ContactPayload[]): Promise<unknown[]> {
    this._$isApiLoading.set(true);
    const QUEUED_REQS = this._buildContactPermissionApiRequestsParameters(updatableContacts);
    return Promise.allSettled(
      QUEUED_REQS.map(({ email, roleId, http }: PermissionRequest) =>
        lastValueFrom<PermissionResponse>(
          http.apply(this._contactSvc, [this._session.account.id, email, roleId]) as Observable<PermissionResponse>
        )
      )
    )
      .then((values: PromiseSettledResult<PermissionResponse>[]) => {
        return Promise.resolve(
          values.map((value: PromiseSettledResult<PermissionResponse>, valueIdx: number) => {
            const QUEUED_REQUEST = QUEUED_REQS[valueIdx];
            const REQUEST = {
              email: QUEUED_REQUEST.email,
              roleId: QUEUED_REQUEST.roleId,
              selected: QUEUED_REQUEST.http.name.toLowerCase().includes('addrole')
            };
            return {
              status: value.status,
              value:
                value.status === 'rejected'
                  ? {
                      error: value.reason,
                      request: REQUEST
                    }
                  : {
                      UserMessage: (value.value as { UserMessage: string }).UserMessage,
                      request: REQUEST
                    }
            };
          })
        );
      })
      .finally(() => this._$isApiLoading.set(false));
  }

  /**
   * @description Collection of logic wrapped to ultimate generate a current "status"
   *   of whether the Admin Acknowledgment is agreed-to or not
   * @returns `Promise<AdminAcknowledgmentResponse>`
   */
  determineAccountAdminAcknowledgedStatus(): Promise<{ status: string; isViaHttpRequest: boolean }> {
    const isAcknowledged =
      this._permissionsSvc.hasPortalEmployeeRole() ||
      this._permissionsSvc.hasPortalAdminRole() ||
      this._permissionsSvc.hasMeasurementEmployeeRole() ||
      this._session.contact.isAdminAgreementAcknowledged;
    return isAcknowledged
      ? Promise.resolve({
          status: 'Agreed',
          isViaHttpRequest: false
        })
      : this._retrieveAccountAdminAcknowledgment();
  }

  /**
   * @description Determines whether this particular **Contact** can sign the ESAA ("yes"),
   *   can't sign the ESAA ("no"), or if it's pending from a previous submission ("pending")
   * @param apiContact `Contact & { OnboardStatus: 'Inactive'|'Invite Sent'|'Invite Expired'|'ESAA Sent'|'Active'; }`
   * @param isOnlyOneContact boolean
   * @returns 'yes'|'no'|'pending'
   */
  private _determineEsaaStatus(
    { OnboardStatus: contactEsaaStatus }: Pick<ContactV2, 'OnboardStatus'>,
    isOnlyOneContact: boolean
  ): 'yes' | 'no' | 'pending' {
    let status: 'yes' | 'no' | 'pending' = 'no';
    if (
      isOnlyOneContact &&
      !this._session.account.isEsaaSigned &&
      (this._permissionsSvc.hasPortalAdminRole() || this._permissionsSvc.hasPortalEmployeeRole())
    ) {
      if (contactEsaaStatus.toLowerCase() === 'esaa sent') {
        status = 'pending';
      } else {
        status = 'yes';
      }
    }
    return status;
  }

  /**
   * @description (Getter) retrieves the current Account's ESAA status
   * @returns string
   */
  get accountEsaaStatus(): string {
    return this._session.account.esaaStatus ?? 'ACCOUNT_ESAA_STATUS';
  }

  /**
   * @description (Getter) retrieves the current Account's ID
   * @returns string
   */
  get accountId(): string {
    return this._session.account.id ?? 'ACCOUNT_ID';
  }

  /**
   * @description (Getter) retrieves the current Account name
   * @returns string
   */
  get accountName(): string {
    return this._session.account.name ?? 'your Account';
  }

  /**
   * @description (Getter)…
   */
  get portalRoleNameMap(): Record<string, string> {
    return this._portalRoleNameMap;
  }

  /**
   * @description Removes Contact, identified by supplied email address, from the
   *   current Account
   * @param contactEmail ReturnType<() => SanitizedContact['email']>
   * @returns Promise<any>
   */
  removeContactFromAccount(contactEmail: string): Promise<{
    UserMessage: string;
  }> {
    this._$isApiLoading.set(true);
    return lastValueFrom<{
      UserMessage: string;
    }>(
      this._contactSvc.deleteRoleFromAccountContact(
        this._session.account.id,
        contactEmail,
        this._session.account.accountAccessPortalRoleId
      )
    ).finally(() => this._$isApiLoading.set(false));
  }

  /**
   * @description Method retrieves the current Account's _Contacts_ and
   *   available _Portal Roles_ from the API
   * @returns `Promise<{
   *   contacts: SanitizedContact[];
   *   portalRoles: SanitizedPortalRole[];
   * }>`
   */
  async getAdminData(): Promise<{
    contacts: SanitizedContact[];
    portalRoles: SanitizedPortalRole[];
    contactsFilterOptions: ContactFilterOption[];
  }> {
    this._$isApiLoading.set(true);
    try {
      const [contactsResult, portalRolesResult] = await Promise.allSettled([
        lastValueFrom(this._accountSvc.getContacts(this._session.account.id)),
        lastValueFrom(this._accountSvc.getAccountRoles(this._session.account.id))
      ]);
      const contacts = contactsResult.status === 'fulfilled' ? (contactsResult.value as ContactV2[]) : [];
      const portalRoles = portalRolesResult.status === 'fulfilled' ? (portalRolesResult.value as PortalRoleV2[]) : [];
      const sanitized = this._sanitizeAdminData({ contacts, portalRoles });
      return sanitized;
    } finally {
      this._$isApiLoading.set(false);
    }
  }

  /**
   * @description Utility method takes the array of Account Contacts(s),
   *   and creates a "slimmed down" version of it with only Portal Role(s)
   * @param contacts SanitizedContact[]
   * @returns `ContactPayload[]`
   */
  getContactsRolesPayload(contacts: Pick<SanitizedContact, 'id' | 'segments'>[]): ContactPayload[] {
    return contacts.map(({ id, segments }) => ({
      id,
      roles: segments.flatMap(({ list }) =>
        list.map(({ id: roleId, selected: isSelected }) => ({
          id: roleId,
          selected: isSelected ?? false
        }))
      )
    }));
  }

  dispatchContactInvitation({
    nameFirst,
    nameLast,
    recipientId
  }: {
    nameFirst: string;
    nameLast: string;
    recipientId: string;
  }): Promise<unknown> {
    return lastValueFrom(
      this._registerSvc.sendInvitationToAccountContact(
        recipientId,
        this._session.account.id,
        nameFirst,
        nameLast,
        this._session.contact.id
      )
    );
  }

  /**
   * @description Utility method generates a DocuSign "envelope" and
   *   returns it
   * @param contact `Omit<SanitizedContact, 'isVisible'>`
   * @returns `Envelope`
   */
  private readonly _getDocusignEnvelope = (contact: Omit<SanitizedContact, 'isVisible'>): Envelope => ({
    SignerName: contact.name.full,
    SignerEmail: contact.email,
    AccountId: this._authSvc.dTest(_env.dsConfig.AccountId),
    ContactId: contact.id,
    TemplateId: this._authSvc.dTest(_env.dsConfig.TemplateId),
    BasePath: _env.dsConfig.BasePath
  });

  /**
   * @description Utility method grabs the (server-generated and cookie-saved)
   *   DocuSign token
   * @returns string
   */
  private _getDocusignToken(): string {
    const isTokenPresent = this._doc.cookie.split(';').some(cookie => cookie.trim().startsWith('DSAccessToken'));
    return isTokenPresent
      ? (this._doc.cookie
          .split(';')
          .find(cookie => cookie.trim().startsWith('DSAccessToken'))
          ?.split('=')
          .at(1) ?? '')
      : '';
  }

  /**
   * @description `computed()` Signal method _sanitizes_ the list of Account
   *   Contact(s), taking into account how that list is **sorted** and/or **filtered**
   * @param sanitizedContacts SanitizedContact[]
   * @param sortValue ContactsSortValue
   * @param filterValue ContactsFilterValue
   * @returns `SanitizedContact[]`
   */
  private _onAllAccountContacts(
    sanitizedContacts: SanitizedContact[],
    filterOptions: ContactFilterOption[],
    searchValue: string,
    sortValue: 'name--a' | 'name--d' | 'login-date--a' | 'login-date--d'
  ): SanitizedContact[] {
    const SEARCH_VALUE = searchValue?.toLowerCase().trim() ?? '';
    const filterOption = filterOptions.find(({ selected: isSelected }) => isSelected === true);
    const FILTER_VALUE = filterOption?.value ?? 'all';
    // First, sort the Contacts array according to the signal value
    const SANITIZED_CONTACTS = sanitizedContacts.sort((contactA, contactB) => {
      let sortResult = 0;
      if (sortValue === 'name--a') {
        sortResult = contactB.name.last.localeCompare(contactA.name.last);
      } else if (sortValue === 'name--d') {
        sortResult = contactA.name.last.localeCompare(contactB.name.last);
      } else if (sortValue === 'login-date--a') {
        sortResult = contactA.loggedInOn.getTime() - contactB.loggedInOn.getTime();
      } else if (sortValue === 'login-date--d') {
        sortResult = contactB.loggedInOn.getTime() - contactA.loggedInOn.getTime();
      }
      return sortResult;
    });
    // Lastly, set the Contact's visibility
    if (SANITIZED_CONTACTS) {
      SANITIZED_CONTACTS.forEach(sanitizedContact => {
        /**
         * @summary "active" vs "inactive" status is determined by two Contact properties
         *   from CRM – the **registered** boolean, and the **onboard** enum. If either
         *   the **registered** property is true _OR_ the **onboard** enum is "active," the
         *   Contact is determined active. Otherwise, if the **registered** property is false
         *   _AND_ the **onboard** enum is anything but "active," Contact is inactive.
         */
        const isVisibilityFilterTrue =
          FILTER_VALUE === 'all' ||
          (FILTER_VALUE === 'segment:nggp' && sanitizedContact.businessSegments.includes('nggp')) ||
          (FILTER_VALUE === 'segment:ngpintrastate' && sanitizedContact.businessSegments.includes('ngpintrastate')) ||
          (FILTER_VALUE === 'status:active' && sanitizedContact.status.crmRecord === 'active') ||
          (FILTER_VALUE === 'status:inactive' && sanitizedContact.status.crmRecord === 'inactive');
        const isVisibilitySearchTrue =
          sanitizedContact.email.toLowerCase().includes(SEARCH_VALUE) ||
          sanitizedContact.name.full.toLowerCase().includes(SEARCH_VALUE);
        sanitizedContact.isVisible = isVisibilityFilterTrue && isVisibilitySearchTrue;
      });
    }
    return structuredClone(SANITIZED_CONTACTS);
  }

  /**
   * @description `computed()` Signal method determines if the _Add New User_ button
   *   should be enabled
   * @param showAdminAcknowledgment boolean
   * @returns boolean
   */
  private _canAddNewContact(canShowAdminAcknowledgment: boolean): boolean {
    return (
      this._$isApiLoading() === false && canShowAdminAcknowledgment === false && this._session.contact.isAdmin === true
    );
  }

  /**
   * @description `computed()` Signal method determines if the _Save User Settings_
   *   button should be enabled
   * @param updatedContacts `ContactPayload[]`
   * @returns boolean
   */
  private _canSaveContacts(updatedContacts: ContactPayload[]): boolean {
    return !this._$isApiLoading() && updatedContacts.length >= 1;
  }

  /**
   * @description …
   */
  private _onLongestContactNameNumChars(visibleContacts: Omit<SanitizedContact, 'isVisible'>[]): number {
    return visibleContacts
      .map(({ name: { full: nameFull } }) => nameFull)
      .reduce((acc: number, currVal: string) => {
        const NAME_LENGTH = currVal.length;
        if (NAME_LENGTH > acc) {
          acc = NAME_LENGTH;
        }
        return acc;
      }, 0);
  }

  /**
   * @description Method handles the caching of Session data for the logged-in
   *   Contact (Admin)
   * @param key 'account'|'user'
   * @param sessionData `SessionAuth`|`Account`
   */
  private _onSessionDefined(key: 'account' | 'user', sessionData: Account | SessionAuth | null): void {
    if (!sessionData) {
      return;
    }
    if (key === 'account') {
      const account = sessionData as Account;
      this._session.account = {
        accountAccessPortalRoleId: '',
        esaaStatus: account.EsaaStatus ?? '',
        id: account.AccountId,
        isEsaaSigned: account.IsEsaaSigned ?? false,
        name: account.AccountName
      };
    } else if (key === 'user') {
      const user = sessionData as SessionAuth;
      if (!user.Contact) {
        return;
      }
      this._session.contact = {
        id: user.Contact.Id ?? '',
        isAdmin: this._permissionsSvc.hasExternalAdminRole() || this._permissionsSvc.hasPortalAdminRole(),
        isAdminAgreementAcknowledged: user.Contact.IsAdminAgreementAcknowledged ?? false,
        isEmployee: user.Contact.IsEmployee ?? false
      };
    }
  }

  /**
   * @description `computed()` Signal method filters the list of Contact(s)
   *   to only include those that are set as visible
   * @param allContacts SanitizedContact[]
   * @returns `Omit<SanitizedContact, 'visible'>[]`
   */
  private _onVisibleAccountContacts(allContacts: SanitizedContact[] = []): Omit<SanitizedContact, 'isVisible'>[] {
    return allContacts
      .filter(contact => contact.isVisible === true)
      .map(contact => {
        const { isVisible: _isVisible, ...cleanedContact } = contact;
        return cleanedContact;
      });
  }

  /**
   * @description `computed()` Signal method reconciles the _current_ list of Contact(s)'
   *   Portal Role (changes) against the _cached_ (from API) list, and returns an array
   *   of _actual_ changes to make to this Account's Contacts
   * @param allContacts SanitizedContact[]
   * @param cachedContactsRoles ContactPayload[]
   * @returns `ContactPayload[]`
   */
  private _onUpdatedContacts(allContacts: SanitizedContact[], cachedContactsRoles: ContactPayload[]): ContactPayload[] {
    const payload: ContactPayload[] = [];
    /**
     * @description Block of code to execute against each Contact for this Account
     * @param contact SantizedContact
     */
    const onEachContact = (contact: SanitizedContact): void => {
      /**
       * @description Block of code to execute against each Portal Role for this Contact
       * @param changedPortalRole `Pick<SanitizedPortalRole, 'id'> & { selected: boolean; }`
       */
      const onEachChangedContactPortalRole = (changedPortalRole: ContactRolePayload): void => {
        const isContactInPayload = payload.some(({ id }) => id === contact.id);
        const CACHED_ROLE = CACHED_PAYLOAD.find(({ id }: ContactRolePayload) => id === changedPortalRole.id);
        if (!CACHED_ROLE) {
          return;
        }
        const isPortalRoleChanged = CACHED_ROLE.selected !== changedPortalRole.selected;
        if (isPortalRoleChanged && isContactInPayload) {
          payload
            .find(({ id }) => id === contact.id)
            ?.roles.push({
              id: changedPortalRole.id,
              selected: changedPortalRole.selected
            });
        } else if (isPortalRoleChanged) {
          payload.push({
            id: contact.id,
            roles: [
              {
                id: changedPortalRole.id,
                selected: changedPortalRole.selected
              }
            ]
          });
        } else if (!isPortalRoleChanged && isContactInPayload) {
          const contactPayload = payload.find(({ id }) => id === contact.id);
          if (!contactPayload) {
            return;
          }
          contactPayload.roles = contactPayload.roles.filter(
            ({ id }: ContactRolePayload) => id !== changedPortalRole.id
          );
          if (!contactPayload.roles.length) {
            payload.splice(
              payload.findIndex(({ id }) => id === contact.id),
              1
            );
          }
        }
      };
      const CHANGED_PAYLOAD = this.getContactsRolesPayload([contact]).at(0)?.roles ?? [];
      const CACHED_PAYLOAD = cachedContactsRoles.find(({ id }: ContactPayload) => id === contact.id)?.roles ?? [];
      CHANGED_PAYLOAD.forEach(onEachChangedContactPortalRole);
    };
    allContacts?.forEach(onEachContact);
    return payload;
  }

  /**
   * @description HTTP (API) method updates this (Admin's) CRM Contact
   *   record to log his/her acceptance of the "Admin Acknowledgment."
   * @returns Promise<any>
   */
  putAccountAdminAcknowledgment(): Promise<unknown> {
    return lastValueFrom(
      this._http.put(
        `${this._baseUrl}/accountadminacknowledgements/accounts/${this._session.account.id}/contacts/${this._session.contact.id}`,
        null
      )
    );
  }

  /**
   * @description Utility method determines the status of this Account's Admin
   *   Acknowledgment acceptance by checking, specifically, via the API
   * @returns `Promise<AdminAcknowledgmentResponse>`
   */
  private _retrieveAccountAdminAcknowledgment(): Promise<AdminAcknowledgmentResponse> {
    const RESPONSE: AdminAcknowledgmentResponse = {
      status: 'Unagreed',
      isViaHttpRequest: true
    };
    this._$isApiLoading.set(true);
    return lastValueFrom(
      this._http.get<AdminAcknowledgmentApiResponse>(
        `${this._baseUrl}/accountadminacknowledgements/accounts/${this._session.account.id}/contacts/${this._session.contact.id}`
      )
    ).then((results: AdminAcknowledgmentApiResponse) => {
      if (results.Status === 'Not Found') {
        return lastValueFrom(
          this._http.post(
            `${this._baseUrl}/accountadminacknowledgements/accounts/${this._session.account.id}/contacts/${this._session.contact.id}`,
            null
          )
        ).then(() => {
          this._$isApiLoading.set(false);
          return RESPONSE;
        });
      } else if (results.Status === 'Found' && results.IsAdminAgreementAcknowledged) {
        RESPONSE.status = 'Agreed';
        this._storageSvc.currentUser$.pipe(take(1)).subscribe(user => {
          if (user?.Contact) {
            user.Contact.IsAdminAgreementAcknowledged = true;
            this._storageSvc.setCurrentUser(user);
          }
        });
      } else if (results.Status !== 'Found') {
        RESPONSE.status = results.Status;
      }
      this._$isApiLoading.set(false);
      return RESPONSE;
    });
  }

  /**
   * @description Method "cleans up" the API data for all the
   *   Account Portal Roles & the list of Account Contact(s)
   * @param adminData
   * @returns `{
   *   contacts: SanitizedContact[];
   *   portalRoles: SanitizedPortalRole[];
   * }`
   */
  private _sanitizeAdminData(adminData: { contacts: ContactV2[]; portalRoles: PortalRoleV2[] }): {
    contacts: SanitizedContact[];
    portalRoles: SanitizedPortalRole[];
    contactsFilterOptions: ContactFilterOption[];
  } {
    /**
     * @description
     */
    const isContactInvitable = (
      onboardStatus: 'inactive' | 'invite sent' | 'invite expired' | 'esaa sent' | 'active',
      isRegistered: boolean,
      contactRoles: PortalRolesGroup[]
    ): boolean => {
      let isInvitable = false;
      if (
        ((!isRegistered &&
          (onboardStatus === 'esaa sent' || onboardStatus === 'inactive' || onboardStatus === 'invite expired')) ||
          onboardStatus === 'invite sent') &&
        contactRoles.some((roleGroup: PortalRolesGroup) =>
          roleGroup.list.some((role: SanitizedPortalRole) => role.selected === true)
        )
      ) {
        isInvitable = true;
      }
      return isInvitable;
    };
    /**
     * @description Function "cleans up" & returns the API data
     *   attached to a particular Account Contact
     * @param apiContact `ContactV2`
     * @returns `SanitizedContact`
     */
    const getSanitizedContactData = (apiContact: ContactV2): SanitizedContact => ({
      businessSegments: apiContact.BusinessSegments.map((segment: string) => segment.toLowerCase()),
      createdOn: new Date(apiContact.CreatedDate),
      ...(apiContact.DocuSignEnvelopeId
        ? {
            docusignId: apiContact.DocuSignEnvelopeId
          }
        : {}),
      loggedInOn: new Date(apiContact.LastSuccesfulLoginDate),
      email: apiContact.Email,
      username: apiContact.UserName,
      id: apiContact.Id,
      esaaStatus: this._determineEsaaStatus(apiContact, adminData.contacts.length <= 1),
      isInvitable: isContactInvitable(
        apiContact.OnboardStatus.toLowerCase() as
          'inactive' | 'invite sent' | 'invite expired' | 'esaa sent' | 'active',
        apiContact.RegisteredStatus,
        reconcilePortalRoles(apiContact.PortalAccountRoles, adminData.portalRoles)
      ),
      isQueuedForUpdate: false,
      isVisible: apiContact.Status.toLocaleLowerCase() === 'active',
      name: {
        first: apiContact.FirstName,
        last: apiContact.LastName,
        full: apiContact.FullName
      },
      segments: reconcilePortalRoles(apiContact.PortalAccountRoles, adminData.portalRoles),
      status: {
        crmRecord: apiContact.Status.toLocaleLowerCase(),
        onboard: apiContact.OnboardStatus.toLowerCase() as
          'inactive' | 'invite sent' | 'invite expired' | 'esaa sent' | 'active',
        registered: apiContact.RegisteredStatus
      }
    });
    /**
     * @description Function returns a "cleaned up" version of a Portal Role
     * @param apiPortalRole `PortalRole & {
     *   Description: string;
     *   PortalRoleId: string;
     *   RoleName: string;
     * }`
     * @returns `SanitizedPortalRole`
     */
    const getSanitizedPortalRoleData = (apiPortalRole: PortalRoleV2): SanitizedPortalRole => {
      if (apiPortalRole.PortalRoleName.toLowerCase() === 'account access') {
        this._session.account.accountAccessPortalRoleId = apiPortalRole.PortalAccountRoleId;
      }
      return {
        description: apiPortalRole.PortalRole.Description,
        id: apiPortalRole.PortalAccountRoleId,
        name: this._portalRoleNameMap[apiPortalRole.PortalRoleName.toLowerCase()] || 'Account Access'
      };
    };
    /**
     * @description Function creates the list(s) of Portal Roles that belong
     *   to the Contact (and sets them as visible or not)
     * @param contactPortalRoles `PortalRole[]`
     * @param accountPortalRoles `PortalRole[]`
     * @returns `PortalRolesGroup[]`
     */
    const reconcilePortalRoles = (
      contactPortalRoles: PortalRoleV2[],
      accountPortalRoles: PortalRoleV2[]
    ): PortalRolesGroup[] => {
      const SANITIZED_CONTACT_PORTAL_ROLES = contactPortalRoles.map(getSanitizedPortalRoleData);
      const SANITIZED_ACCOUNT_PORTAL_ROLES = accountPortalRoles.map(getSanitizedPortalRoleData);
      const isNgpRolesExist = SANITIZED_ACCOUNT_PORTAL_ROLES.some(
        accountPortalRole => accountPortalRole.name.toLowerCase() === 'requests'
      );
      const isNggpRolesExist = SANITIZED_ACCOUNT_PORTAL_ROLES.some(
        accountPortalRole => !['requests', 'account access'].includes(accountPortalRole.name.toLowerCase())
      );
      const reconciledPortalRoles: PortalRolesGroup[] = [];
      if (isNgpRolesExist) {
        reconciledPortalRoles.push({
          name: 'Pipelines',
          list: SANITIZED_ACCOUNT_PORTAL_ROLES.filter(
            accountPortalRole => accountPortalRole.name.toLowerCase() === 'requests'
          )
            .map(accountNgpPortalRole => {
              accountNgpPortalRole.selected = SANITIZED_CONTACT_PORTAL_ROLES.some(
                contactPortalRole => contactPortalRole.id === accountNgpPortalRole.id
              );
              return accountNgpPortalRole;
            })
            .sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB)),
          visible: true
        });
      }
      if (isNggpRolesExist) {
        reconciledPortalRoles.push({
          name: 'Gathering & Processing',
          list: SANITIZED_ACCOUNT_PORTAL_ROLES.filter(
            accountPortalRole => !['requests', 'account access'].includes(accountPortalRole.name.toLowerCase())
          )
            .map(accountNggpPortalRole => {
              accountNggpPortalRole.selected = SANITIZED_CONTACT_PORTAL_ROLES.some(
                contactPortalRole => contactPortalRole.id === accountNggpPortalRole.id
              );
              return accountNggpPortalRole;
            })
            .sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB)),
          visible: true
        });
      }
      return reconciledPortalRoles.sort(({ name: nameA }, { name: nameB }) =>
        nameA < nameB ? -1 : nameA > nameB ? 1 : 0
      );
    };
    const SANITIZED_ADMIN_DATA: {
      contacts: SanitizedContact[];
      portalRoles: SanitizedPortalRole[];
      contactsFilterOptions: ContactFilterOption[];
    } = {
      contacts: adminData.contacts.map(getSanitizedContactData),
      portalRoles: adminData.portalRoles
        .map(getSanitizedPortalRoleData)
        .sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB)),
      contactsFilterOptions: []
    };
    SANITIZED_ADMIN_DATA.contacts.forEach(sanitizedContact => {
      const hasGatheringAndProcessingRoles = sanitizedContact.segments.some(
        ({ name }: PortalRolesGroup) => name === 'Gathering & Processing'
      );
      const hasPipelinesRoles = sanitizedContact.segments.some(({ name }: PortalRolesGroup) => name === 'Pipelines');
      const isGatheringAndProcessingCached = SANITIZED_ADMIN_DATA.contactsFilterOptions.some(
        ({ value }) => value === 'segment:nggp'
      );
      const isPipelinesCached = SANITIZED_ADMIN_DATA.contactsFilterOptions.some(
        ({ value }) => value === 'segment:ngpintrastate'
      );
      if (hasGatheringAndProcessingRoles && !isGatheringAndProcessingCached) {
        SANITIZED_ADMIN_DATA.contactsFilterOptions.push({
          label: 'G&P Access',
          selected: false,
          value: 'segment:nggp'
        });
      }
      if (hasPipelinesRoles && !isPipelinesCached) {
        SANITIZED_ADMIN_DATA.contactsFilterOptions.push({
          label: 'Pipelines Access',
          selected: false,
          value: 'segment:ngpintrastate'
        });
      }
    });
    return SANITIZED_ADMIN_DATA;
  }

  /**
   * @todo **_CLEAN THIS UP_**
   * @description Collection of logic/process(es) for sending an ESAA
   *   agreement to a chosen contact
   * @param contact `Omit<SanitizedContact, 'isVisible'>`
   * @returns `Promise<any>`
   */
  sendEsaa(contact: Omit<SanitizedContact, 'isVisible'>): Promise<unknown> | undefined {
    this._$isApiLoading.set(true);
    const DOCUSIGN_TOKEN = this._getDocusignToken();
    const DOCUSIGN_ENVELOPE = this._getDocusignEnvelope(contact);
    const onPostDocusignEnvelopeSuccess = (response: DocuSignEnvelopeResponse): void => {
      this._$isApiLoading.set(false);
      this._storageSvc.currentAccount$.pipe(take(1)).subscribe(account => {
        if (!account) {
          return;
        }
        account.EsaaStatus = 'Sent';
        account.DocuSignEnvelopeId = response.EnvelopeId;
        this._storageSvc.storeCurrentAccount(account);
      });
    };
    if (DOCUSIGN_ENVELOPE && !!DOCUSIGN_TOKEN === false) {
      const onPostDocusignAuthenticateSuccess = (
        response: DocuSignAuthenticateResponse
      ): Promise<DocuSignEnvelopeResponse> => {
        const DATE = new Date();
        DATE.setTime(DATE.getTime() + 30 * 60 * 1000);
        this._doc.cookie = `DSAccessToken=${response.AccessToken};expires=${DATE.toUTCString()}`;
        return lastValueFrom<DocuSignEnvelopeResponse>(
          this._docusignSvc.postDocuSignEnvelope(
            DOCUSIGN_ENVELOPE,
            this._session.account.id
          ) as Observable<DocuSignEnvelopeResponse>
        );
      };
      return lastValueFrom<DocuSignAuthenticateResponse>(
        this._docusignSvc.postDocuSignAuthenticate(this._session.account.id) as Observable<DocuSignAuthenticateResponse>
      )
        .then(onPostDocusignAuthenticateSuccess)
        .then(onPostDocusignEnvelopeSuccess);
    } else if (DOCUSIGN_ENVELOPE) {
      return lastValueFrom<DocuSignEnvelopeResponse>(
        this._docusignSvc.postDocuSignEnvelope(
          DOCUSIGN_ENVELOPE,
          this._session.account.id
        ) as Observable<DocuSignEnvelopeResponse>
      ).then(onPostDocusignEnvelopeSuccess);
    }
  }
}
